import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRevAllNodesWithStatus } from "../../../redux/procedureRevisionSlice";

/**
 * useStepData Hook
 * 
 * Fetches revision nodes and calculates step title, subheading, and sibling order
 * 
 * @param {Object} params
 * @param {string} params.partId - Part ID
 * @param {string} params.revisionId - Revision ID
 * @param {string} params.section_id_ref - Section ID (tree_node_id)
 * @param {string} params.step_id_ref - Step ID (optional)
 * @param {string} params.partnumber - Part number for subheading
 * @param {string} params.revision - Revision for subheading
 * 
 * @returns {Object} { loading, allNodesData, stepTitle, sectionTitle, sectionIdRef, subheading, siblingOrder }
 */
const useStepData = ({ partId, revisionId, section_id_ref, step_id_ref, partnumber, revision, mode }) => {
  const dispatch = useDispatch();
  const [allNodesData, setAllNodesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stepTitle, setStepTitle] = useState("Step");
  const [sectionTitle, setSectionTitle] = useState("Section");
  const [sectionIdRef, setSectionIdRef] = useState(null);
  const [subheading, setSubheading] = useState("");
  const [siblingOrder, setSiblingOrder] = useState(null);
  const [currentStepData, setCurrentStepData] = useState(null);
  const { currentStatus,item_code_type } = useSelector((state) => state.procedureRevision);

  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoading(true);
        const result = await dispatch(
          getRevAllNodesWithStatus({
            partId: partId,
            revisionId: revisionId,
          })
        );
        if (result.payload) {
          setAllNodesData(result.payload.data);

          // Calculate step number and section index
          let totalSteps = 0;
          let currentSectionIndex = 0;
          let stepIndex = 0;
          let foundStep = false;
          let stptitle = ""

          // Sort sections by sibling_order
          const sortedSections = result.payload.data

          // Find section index
          currentSectionIndex = sortedSections.findIndex(
            (section) => section.tree_node_id === section_id_ref
          );
          const currentSection = sortedSections[currentSectionIndex];
          setSectionIdRef(currentSection.section_id_ref);

          // Calculate sibling order
          let calculatedSiblingOrder = null;
          if (step_id_ref) {
            // Find the step and get its sibling_order + 1
            if (currentSection?.steps && Array.isArray(currentSection.steps)) {
              const targetStep = currentSection.steps.find(
                (step) => step.tree_node_id === step_id_ref
              );
              if (targetStep) {
                const stepSiblingOrder = targetStep.sibling_order || targetStep.procedure_tree?.sibling_order || 0;
                calculatedSiblingOrder = parseInt(stepSiblingOrder, 10) + 1;
              }
            }
          } else {
            // Count total steps in current section + 1
            if (currentSection?.steps && Array.isArray(currentSection.steps)) {
              calculatedSiblingOrder = currentSection.steps.length + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          }
          setSiblingOrder(calculatedSiblingOrder);

          // Calculate total steps or find specific step
          if (step_id_ref && mode === "edit") {
            // Find the step with step_id_ref
            for (const section of sortedSections) {
              if (section.steps && Array.isArray(section.steps)) {
                for (let i = 0; i < section.steps.length; i++) {
                  const step = section.steps[i];
                  if (step.tree_node_id === step_id_ref) {
                    stepIndex = totalSteps + i + 1;
                    foundStep = true;
                    setCurrentStepData(step); // Store the step data for edit mode
                    break;
                  }
                }
                if (foundStep) break;
                totalSteps += section.steps.length;
              }
            }
            stptitle = `Step ${stepIndex}`;
          } else if(mode === "add" && step_id_ref || mode === "add") {
            // Calculate total steps across all sections
            for (const section of sortedSections) {
              if (section.steps && Array.isArray(section.steps)) {
                totalSteps += section.steps.length;
              }
            }
            stptitle = `Step ${totalSteps + 1}`;
          }
          setStepTitle(stptitle);
          // Build subheading
          const sectionNumber = currentSectionIndex + 1;
          setSectionTitle(`Section ${sectionNumber}`);
          const subheadingText = `${partnumber} > Rev ${revision} > Section ${sectionNumber} > ${stptitle}`;
          setSubheading(subheadingText);
        }
      } catch (error) {
        console.error("Error fetching revision nodes:", error);
      } finally {
        setLoading(false);
      }
    };

    if (partId && revisionId && section_id_ref) {
      fetchNodes();
    }
  }, [partId, revisionId, section_id_ref, step_id_ref, dispatch, partnumber, revision]);

  return {
    loading,
    allNodesData,
    stepTitle,
    sectionTitle,
    sectionIdRef,
    subheading,
    siblingOrder:siblingOrder?.toString(),
    currentStepData,
    currentStatus,
    item_code_type
  };
};

export default useStepData;

